<script>
  import { onMount } from 'svelte';

  import {
    Button,
    Column,
    DataTable,
    Grid,
    InlineNotification,
    Row,
    StructuredList,
    StructuredListHead,
    StructuredListRow,
    StructuredListCell,
    StructuredListBody,
    Tab,
    TabContent,
    Tabs,
  } from 'carbon-components-svelte';

  import LoadingButton from '../../components/LoadingButton.svelte';
  import LoadingSpinner from '../../components/LoadingSpinner.svelte';

  import { clearAllCache, getCacheStats, getSystemStats } from '../../services';
  import { appConfig, messagesStore } from '../../stores';

  let pageData = { loading: true };
  let submissionData = { loading: false };
  let systemData = { loading: true };

  const MEMORY_LABELS = {
    heapUsedMb: 'Heap used',
    heapLimitMb: 'Heap limit',
    oldSpaceMb: 'Old space',
    largeObjectSpaceMb: 'Large object space',
    externalMb: 'External',
    rssMb: 'RSS',
  };

  const POOL_LABELS = {
    totalWorkers: 'Total workers',
    busyWorkers: 'Busy workers',
    idleWorkers: 'Idle workers',
    pendingTasks: 'Pending tasks',
    activeTasks: 'Active tasks',
  };

  async function clearCache() {
    submissionData = { loading: true };
    try {
      await clearAllCache();
      submissionData = { loading: false };
      await retrieveCacheStats();
    } catch (error) {
      console.error('[AdminDashboard] Failed to clear cache!', error);
      submissionData = { ...submissionData, error, errorMessage: 'Failed to clear cache!', loading: false };
    }
  }

  async function retrieveCacheStats() {
    try {
      const cacheStats = await getCacheStats();
      pageData = { cacheStats, loading: false };
    } catch (error) {
      console.error('[AdminDashboard] Failed to load stats!', error);
      pageData = { loading: false, error };
    }
  }

  async function loadSystemStats() {
    systemData = { ...systemData, loading: true };
    try {
      const stats = await getSystemStats();
      systemData = { stats, loading: false };
    } catch (error) {
      console.error('[AdminBasicInformation] Failed to load system stats!', error);
      systemData = { loading: false, error };
    }
  }

  onMount(() => {
    retrieveCacheStats();
    loadSystemStats();
  });

  $: memoryRows = systemData.stats
    ? Object.entries(MEMORY_LABELS).map(([key, metric]) => ({ id: key, metric, value: systemData.stats.mainThreadMemory?.[key] ?? 'N/A' }))
    : [];

  $: poolRows = systemData.stats
    ? Object.entries(POOL_LABELS).map(([key, metric]) => ({ id: key, metric, value: systemData.stats.workerPool?.[key] ?? 'N/A' }))
    : [];

  $: activeUserCount = Object.values($messagesStore?.usersActivity ?? {}).filter((user) => user.status === 'ONLINE').length;
  $: idleUserCount = Object.keys($messagesStore?.usersActivity ?? {}).length - activeUserCount;
</script>

{#if pageData.error}
  <InlineNotification hideCloseButton kind="error" lowContrast title="Error:" subtitle="Failed to load stats!" />
{:else}
  <LoadingSpinner loading={pageData.loading}>
    <Grid noGutterLeft noGutterRight padding>
      <Row>
        <Column sm={4} md={8} lg={8}>
          <h2 class="column-heading h4">Overview</h2>
          <StructuredList condensed flush class="mst-structured-list--truncate">
            <StructuredListHead>
              <StructuredListRow head>
                <StructuredListCell head>Key</StructuredListCell>
                <StructuredListCell head>Value</StructuredListCell>
              </StructuredListRow>
            </StructuredListHead>
            <StructuredListBody>
              <StructuredListRow>
                <StructuredListCell noWrap>Application Version</StructuredListCell>
                <StructuredListCell>{$appConfig.data.versionInfo.version}</StructuredListCell>
              </StructuredListRow>
              <StructuredListRow>
                <StructuredListCell noWrap>Application Branch</StructuredListCell>
                <StructuredListCell>{$appConfig.data.versionInfo.branch}</StructuredListCell>
              </StructuredListRow>
              <StructuredListRow>
                <StructuredListCell noWrap>Application Pipeline</StructuredListCell>
                <StructuredListCell>{$appConfig.data.versionInfo.pipeline}</StructuredListCell>
              </StructuredListRow>
              <StructuredListRow>
                <StructuredListCell noWrap>Active Users</StructuredListCell>
                <StructuredListCell>{activeUserCount}</StructuredListCell>
              </StructuredListRow>
              <StructuredListRow>
                <StructuredListCell noWrap>Idle Users</StructuredListCell>
                <StructuredListCell>{idleUserCount}</StructuredListCell>
              </StructuredListRow>
              <StructuredListRow>
                <StructuredListCell noWrap>Data Feed Inventory Hash</StructuredListCell>
                <StructuredListCell>{pageData?.cacheStats?.inventoryHash ?? 'N/A'}</StructuredListCell>
              </StructuredListRow>
              <StructuredListRow>
                <StructuredListCell noWrap>Cached Tasks</StructuredListCell>
                <StructuredListCell>{pageData?.cacheStats?.tasks}</StructuredListCell>
              </StructuredListRow>
              <StructuredListRow>
                <StructuredListCell noWrap>Cached Users</StructuredListCell>
                <StructuredListCell>{pageData?.cacheStats?.users}</StructuredListCell>
              </StructuredListRow>
            </StructuredListBody>
          </StructuredList>
        </Column>
        <Column sm={4} md={8} lg={8}>
          <div class="row">
            <h2 class="column-heading h4">System Stats</h2>
            {#if systemData.error}
              <InlineNotification hideCloseButton kind="error" lowContrast title="Error:" subtitle="Failed to load system stats!" />
            {/if}
            <div class="system-stats-tabs">
              <Tabs fullWidth type="container">
                <Tab label="Main thread" />
                <Tab label="Worker pool" />
                <svelte:fragment slot="content">
                  <div class="system-stats-actions">
                    <Button size="small" kind="secondary" on:click={loadSystemStats} disabled={systemData.loading}>Refresh</Button>
                  </div>
                  <LoadingSpinner loading={systemData.loading}>
                    {#if systemData.error}
                      <InlineNotification hideCloseButton kind="error" lowContrast title="Error:" subtitle="Failed to load system stats!" />
                    {/if}
                    <TabContent>
                      <DataTable
                        size="short"
                        headers={[
                          { key: 'metric', value: 'Metric' },
                          { key: 'value', value: 'Value (MB)' },
                        ]}
                        rows={memoryRows}
                      />
                    </TabContent>
                    <TabContent>
                      <DataTable
                        size="short"
                        headers={[
                          { key: 'metric', value: 'Metric' },
                          { key: 'value', value: 'Value' },
                        ]}
                        rows={poolRows}
                      />
                    </TabContent>
                  </LoadingSpinner>
                </svelte:fragment>
              </Tabs>
            </div>
          </div>
          <div class="row">
            {#if submissionData.error}
              <InlineNotification hideCloseButton kind="error" lowContrast title="Error:" subtitle={submissionData.errorMessage} />
            {/if}
            <h2 class="column-heading h4">Actions</h2>
            <p class="text-info">
              The server maintains a cache of user subject IDs authorized to access the application, as well as user display names for
              general use. Also, an app level cache is maintained for long-lived tokens. Clearing the cache forces the server to re-fetch
              this information from the database on the next request, which can help resolve issues related to stale data but may
              temporarily increase response times for affected requests.
            </p>
            <LoadingButton on:click={clearCache} disabled={submissionData.loading} isLoading={submissionData.loading}>
              Clear cache
            </LoadingButton>
          </div>
        </Column>
      </Row>
    </Grid>
  </LoadingSpinner>
{/if}

<style>
  .column-heading {
    margin-bottom: 1rem;
  }

  .row {
    margin-bottom: 1rem;
  }

  .system-stats-tabs :global(.bx--tab-content) {
    background-color: var(--cds-ui-01);
  }

  .system-stats-actions :global(.bx--btn) {
    width: 100%;
    max-width: unset;
    justify-content: center;
    text-align: center;
    font-size: 0.65rem;
    padding: 0 10px !important;
    min-height: 1rem;
  }

  .text-info {
    color: var(--cds-text-02);
    display: block;
    font-size: 0.875rem;
    font-weight: 400;
    margin-bottom: 1rem;
  }
</style>
